import React, { useEffect, useRef, useState } from "react";
import { MagnifyingGlass, Bell, Sun, Moon, Command } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import { useTheme } from "@/context/ThemeContext";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { ROLE_BADGE } from "@/lib/roles";
import LanguageSwitcher from "@/components/LanguageSwitcher";

export default function Topbar() {
  const { theme, toggle } = useTheme();
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);
  const lastLatestId = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/notifications");
      setNotifications(data);
      const u = data.filter((n) => !n.read).length;
      setUnread(u);
      // Show toast if a brand-new notification arrived
      if (data.length > 0) {
        const latest = data[0];
        if (lastLatestId.current && lastLatestId.current !== latest.id && !latest.read) {
          toast(latest.title, { description: latest.message, duration: 6000 });
        }
        lastLatestId.current = latest.id;
      }
    } catch {}
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 20000); // live polling every 20s
    return () => clearInterval(t);
  }, []);

  const markRead = async (id) => {
    await api.post(`/notifications/${id}/read`);
    setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
    setUnread((u) => Math.max(0, u - 1));
  };

  const badge = user && ROLE_BADGE[user.role];

  return (
    <header
      data-testid="topbar"
      className="sticky top-0 z-30 h-16 glass px-6 flex items-center gap-4"
    >
      {/* Search */}
      <div className="flex-1 max-w-xl">
        <div className="relative">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            data-testid="global-search"
            type="text"
            placeholder={t("topbar.searchPlaceholder")}
            className="w-full h-10 pl-10 pr-16 rounded-md bg-secondary/60 border border-border/60 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
          />
          <kbd className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1 text-[10px] text-muted-foreground bg-background border border-border rounded px-1.5 py-0.5">
            <Command size={10} /> K
          </kbd>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <LanguageSwitcher compact />
        <button
          data-testid="theme-toggle"
          onClick={toggle}
          aria-label={t("topbar.toggleTheme")}
          className="w-10 h-10 rounded-md flex items-center justify-center text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors"
        >
          {theme === "dark" ? <Sun size={18} weight="duotone" /> : <Moon size={18} weight="duotone" />}
        </button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              data-testid="notification-btn"
              className="relative w-10 h-10 rounded-md flex items-center justify-center text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors"
            >
              <Bell size={18} weight="duotone" />
              {unread > 0 && (
                <span className="absolute top-1.5 right-1.5 min-w-[16px] h-4 px-1 bg-destructive text-destructive-foreground text-[10px] font-bold rounded-full flex items-center justify-center">
                  {unread}
                </span>
              )}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-80">
            <DropdownMenuLabel className="flex items-center justify-between">
              <span>{t("topbar.notifications")}</span>
              <span className="text-[10px] font-normal text-muted-foreground">{t("topbar.unread", { count: unread })}</span>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <div className="max-h-80 overflow-y-auto scrollbar-thin">
              {notifications.length === 0 && (
                <div className="p-6 text-center text-sm text-muted-foreground">{t("topbar.noNotifications")}</div>
              )}
              {notifications.map((n) => (
                <button
                  key={n.id}
                  data-testid={`notif-item-${n.id}`}
                  onClick={() => markRead(n.id)}
                  className={cn(
                    "w-full text-left px-3 py-2.5 rounded-md hover:bg-secondary transition-colors",
                    !n.read && "bg-secondary/40",
                  )}
                >
                  <div className="flex items-start gap-2">
                    {!n.read && <span className="mt-1.5 w-2 h-2 rounded-full bg-destructive shrink-0" />}
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold truncate">{n.title}</div>
                      <div className="text-xs text-muted-foreground line-clamp-2">{n.message}</div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </DropdownMenuContent>
        </DropdownMenu>

        {user && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="user-menu-btn"
                className="ml-2 flex items-center gap-3 h-10 pl-1.5 pr-3 rounded-full hover:bg-secondary transition-colors"
              >
                <div className="w-8 h-8 rounded-full bg-secondary overflow-hidden flex items-center justify-center">
                  {user.avatar ? (
                    <img src={user.avatar} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-xs font-bold">{user.name?.charAt(0)}</span>
                  )}
                </div>
                <div className="hidden sm:block text-left">
                  <div className="text-xs font-semibold leading-tight">{user.name}</div>
                  <div className="text-[10px] text-muted-foreground">{t(`roles.${user.role}`, { defaultValue: badge?.label })}</div>
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div className="font-semibold">{user.name}</div>
                <div className="text-[10px] text-muted-foreground font-normal">{user.email}</div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem>{t("topbar.profile")}</DropdownMenuItem>
              <DropdownMenuItem>{t("topbar.settings")}</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={logout} className="text-destructive">{t("topbar.signOut")}</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </header>
  );
}
